import { motion, AnimatePresence } from "framer-motion";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import logoSloganImg from "../assets/marca/logo-slogan-beige.svg";
import emblema4Img from "../assets/emblema-4.svg";
import logoUqoImg from "../assets/logo-uqo.svg";
import underlineBeigeImg from "../assets/underline-beige.svg";

type FooterLinkItem = {
  id: string;
  label: string;
  type: "section" | "route";
  target: string;
};

function InstagramIcon() {
  return (
    <div className="relative shrink-0 size-[20px] flex items-center justify-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#113F7F"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
      </svg>
    </div>
  );
}

function LocationIcon() {
  return (
    <div className="overflow-clip relative shrink-0 size-[32px] flex items-center justify-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#F2F1DE"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    </div>
  );
}

function FooterNavLink({
  text,
  isHovered,
  onMouseEnter,
  onMouseLeave,
  onClick,
  width,
}: {
  text: string;
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  width?: string;
}) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`relative flex flex-col items-center ${width || ""}`}
      data-name="LINK - NAV"
      type="button"
    >
      <div
        className={`relative flex h-[56px] items-center justify-center p-[16px] ${width || ""}`}
      >
        <p
          className={`font-['Roboto_Mono',monospace] leading-[24px] text-[#f2f1de] text-[16px] tracking-[0.32px] whitespace-nowrap transition-all duration-300 hover:text-white ${isHovered ? "font-bold" : "font-normal"
            }`}
        >
          {text}
        </p>
      </div>

      <AnimatePresence>
        {isHovered && (
          <motion.img
            src={underlineBeigeImg}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
            className="absolute left-1/2 top-1/2 h-[41px] w-[122px] -translate-x-1/2 -translate-y-1/2"
            alt=""
          />
        )}
      </AnimatePresence>
    </button>
  );
}

function TextContainerDesktop() {
  return (
    <div className="content-stretch flex flex-col h-[203.224px] items-start relative shrink-0">
      <div className="h-[203.224px] relative shrink-0 w-[340px]">
        <img
          src={logoSloganImg}
          alt="Logo y slogan"
          className="w-full h-full object-contain"
        />
      </div>
    </div>
  );
}

function FooterAddressContainerDesktop() {
  return (
    <a href="https://maps.app.goo.gl/pkuJdAcC4pV9AVHy7" target="_blank"
      rel="noopener noreferrer" className="content-stretch flex gap-[8px] items-center justify-center relative shrink-0">
      <LocationIcon />
      <p className="font-['Roboto_Mono',monospace] font-normal leading-[0] relative shrink-0 text-[#f2f1de] text-[20px] tracking-[0.4px] whitespace-nowrap">
        <span className="font-['Roboto_Mono',monospace] font-bold leading-[32px]">
          Mariano Moreno 585
        </span>
        <span className="leading-[32px]">, Mendoza</span>
      </p>
    </a>
  );
}

function FooterAddressDesktop() {
  return (
    <div className="content-stretch flex gap-[24px] items-start justify-center relative shrink-0 w-full">
      <a href="https://www.instagram.com/stacyburgers/" target="_blank"
        rel="noopener noreferrer" className="bg-[#f2f1de] content-stretch flex gap-[10px] items-center justify-center p-[8px] relative rounded-[8px] shrink-0 cursor-pointer hover:bg-white transition-colors duration-300">
        <InstagramIcon />
      </a>
      <FooterAddressContainerDesktop />
    </div>
  );
}

function FooterNavigationDesktop({
  links,
  hoveredLink,
  setHoveredLink,
  onLinkClick,
}: {
  links: FooterLinkItem[];
  hoveredLink: string | null;
  setHoveredLink: (value: string | null) => void;
  onLinkClick: (link: FooterLinkItem) => void;
}) {
  return (
    <div className="bg-[rgba(0,0,0,0.2)] relative rounded-[8px] shadow-[0px_10px_32px_0px_rgba(24,24,25,0.1),0px_6px_14px_0px_rgba(24,24,25,0.12)] shrink-0 w-full">
      <div className="flex flex-row items-center size-full">
        <div className="content-center flex flex-wrap items-center justify-center px-[16px] relative w-full">
          {links.map((link) => (
            <FooterNavLink
              key={link.id}
              text={link.label}
              isHovered={hoveredLink === link.label}
              onMouseEnter={() => setHoveredLink(link.label)}
              onMouseLeave={() => setHoveredLink(null)}
              onClick={() => onLinkClick(link)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function FooterLinksDesktop({
  links,
  hoveredLink,
  setHoveredLink,
  onLinkClick,
}: {
  links: FooterLinkItem[];
  hoveredLink: string | null;
  setHoveredLink: (value: string | null) => void;
  onLinkClick: (link: FooterLinkItem) => void;
}) {
  return (
    <div className="content-stretch flex flex-[1_0_0] flex-col gap-[48px] items-center justify-center min-h-px min-w-[328px] relative">
      <FooterAddressDesktop />
      <FooterNavigationDesktop
        links={links}
        hoveredLink={hoveredLink}
        setHoveredLink={setHoveredLink}
        onLinkClick={onLinkClick}
      />
    </div>
  );
}

function TextContainerMobile() {
  return (
    <div className="content-stretch flex w-full flex-col items-center relative shrink-0">
      <div className="relative shrink-0 w-full max-w-[280px] sm:max-w-[320px] md:max-w-[360px]">
        <img
          src={logoSloganImg}
          alt="Logo y slogan"
          className="w-full h-auto object-contain"
        />
      </div>
    </div>
  );
}

function FrameMobile() {
  return (
    <a href="https://maps.app.goo.gl/pkuJdAcC4pV9AVHy7" target="_blank"
      rel="noopener noreferrer" className="content-stretch flex gap-[8px] items-center sm:items-center justify-center  ">
      <LocationIcon />
      <p className="flex-[1_0_0] font-['Roboto_Mono',monospace] font-normal leading-[0] min-h-px min-w-px  text-[#f2f1de] text-[16px] sm:text-[18px] md:text-[20px] tracking-[0.32px] sm:tracking-[0.36px] md:tracking-[0.4px]">
        <span className="font-['Roboto_Mono',monospace] font-bold leading-[24px] sm:leading-[28px] md:leading-[32px]">
          Mariano Moreno 585
        </span>
        <span className="leading-[24px] sm:leading-[28px] md:leading-[32px]">
          , Mendoza
        </span>
      </p>
    </a>
  );
}

function Frame3Mobile() {
  return (
    <div className="content-stretch flex flex-col gap-[24px] items-center relative shrink-0 w-full">
      <FrameMobile />
      <div className="bg-[#f2f1de] content-stretch flex gap-[10px] items-center justify-center p-[8px] relative rounded-[8px] shrink-0 cursor-pointer hover:bg-white transition-colors duration-300">
        <InstagramIcon />
      </div>
    </div>
  );
}

function Frame1Mobile({
  links,
  hoveredLink,
  setHoveredLink,
  onLinkClick,
}: {
  links: FooterLinkItem[];
  hoveredLink: string | null;
  setHoveredLink: (value: string | null) => void;
  onLinkClick: (link: FooterLinkItem) => void;
}) {
  return (
    <div className="bg-[rgba(0,0,0,0.2)] relative rounded-[8px] shadow-[0px_10px_32px_0px_rgba(24,24,25,0.1),0px_6px_14px_0px_rgba(24,24,25,0.12)] shrink-0 w-full">
      <div className="flex flex-row items-center size-full">
        <div className="content-center flex flex-wrap items-center justify-center sm:justify-between gap-y-[4px] px-[12px] sm:px-[16px] relative w-full">
          {links.map((link) => (
            <FooterNavLink
              key={link.id}
              text={link.label}
              width="w-[122px]"
              isHovered={hoveredLink === link.label}
              onMouseEnter={() => setHoveredLink(link.label)}
              onMouseLeave={() => setHoveredLink(null)}
              onClick={() => onLinkClick(link)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Frame2Mobile({
  links,
  hoveredLink,
  setHoveredLink,
  onLinkClick,
}: {
  links: FooterLinkItem[];
  hoveredLink: string | null;
  setHoveredLink: (value: string | null) => void;
  onLinkClick: (link: FooterLinkItem) => void;
}) {
  return (
    <div className="content-stretch flex flex-col gap-[48px] items-center justify-center min-w-[328px] relative shrink-0 w-full">
      <Frame1Mobile
        links={links}
        hoveredLink={hoveredLink}
        setHoveredLink={setHoveredLink}
        onLinkClick={onLinkClick}
      />
      <Frame3Mobile />
    </div>
  );
}

function LogoVolverAJugar() {
  return (
    <div className="relative shrink-0 w-full max-w-[240px] sm:max-w-[280px] md:max-w-[320px] lg:w-[340px] lg:max-w-none">
      <img
        src={emblema4Img}
        alt="Volver a jugar"
        className="w-full h-auto object-contain"
      />
    </div>
  );
}

function MarcaFull() {
  return (
    <a href="https://www.uqostudio.com/" target="_blank"
      rel="noopener noreferrer" className="h-auto relative shrink-0 w-[70px] sm:w-[78px]">
      <img src={logoUqoImg} alt="UQO" className="w-full h-auto object-contain" />
    </a>
  );
}

function FooterCredits() {
  return (
    <div className="content-stretch flex flex-wrap justify-center gap-x-[16px] gap-y-[8px] items-center relative shrink-0 text-center">
      <p className="font-['Roboto_Mono',monospace] font-normal leading-[20px] sm:leading-[24px] relative shrink-0 text-[14px] sm:text-[16px] text-white tracking-[0.28px] sm:tracking-[0.32px] whitespace-nowrap">
        MADE WITH LOVE BY
      </p>
      <MarcaFull />
    </div>
  );
}

export default function Footer() {
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);

  const location = useLocation();
  const navigate = useNavigate();

  const isAboutPage = location.pathname === "/about";

  const footerLinks = useMemo<FooterLinkItem[]>(() => {
    if (isAboutPage) {
      return [
        { id: "home-route", label: "INICIO", type: "route", target: "/" },
        /*  { id: "about-top", label: "ABOUT", type: "section", target: "about-top" }, */
      ];
    }

    return [
      { id: "inicio", label: "INICIO", type: "section", target: "inicio" },
      { id: "comunidad", label: "COMUNIDAD", type: "section", target: "comunidad" },
      { id: "burgers", label: "LAS BURGERS", type: "section", target: "burgers" },
      { id: "about", label: "NOSOTROS", type: "section", target: "about" },
      { id: "foodtruck", label: "FOODTRUCK", type: "section", target: "foodtruck" },
      { id: "equipo", label: "EQUIPO", type: "section", target: "equipo" },
    ];
  }, [isAboutPage]);

  const handleLinkClick = (link: FooterLinkItem) => {
    if (link.type === "route") {
      navigate(link.target);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const element = document.getElementById(link.target);

    if (element) {
      const footerOffset = 96;
      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementTop - footerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <section className="bg-[#113f7f] content-stretch flex flex-col items-center justify-center relative size-full overflow-hidden">
      {/* Desktop */}
      <div className="hidden lg:flex w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          className="flex flex-col gap-[64px] items-center max-w-[1920px] px-[40px] py-[120px] w-full mx-auto"
        >
          <div className="content-stretch flex gap-[64px] items-center w-full">
            <TextContainerDesktop />

            <FooterLinksDesktop
              links={footerLinks}
              hoveredLink={hoveredLink}
              setHoveredLink={setHoveredLink}
              onLinkClick={handleLinkClick}
            />

            <LogoVolverAJugar />
          </div>

          <FooterCredits />
        </motion.div>
      </div>

      {/* Mobile */}
      <div className="lg:hidden w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          className="content-stretch flex flex-col gap-[48px] sm:gap-[56px] md:gap-[64px] items-center max-w-[1920px] px-[16px] sm:px-[20px] md:px-[24px] py-[88px] sm:py-[104px] md:py-[120px] w-full mx-auto"
        >
          <TextContainerMobile />

          <Frame2Mobile
            links={footerLinks}
            hoveredLink={hoveredLink}
            setHoveredLink={setHoveredLink}
            onLinkClick={handleLinkClick}
          />

          <LogoVolverAJugar />
          <FooterCredits />
        </motion.div>
      </div>
    </section>
  );
}